import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ClipboardPen, Plus, Loader2, Trash2, Link2, Search, CloudUpload, Leaf, CheckCircle } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { Card, CardContent } from '../../components/ui/Card';
import QuickEvaluationForm from '../../components/cultivar/QuickEvaluationForm';
import cultivarService from '../../services/cultivar';
import evaluationQueue, { type PendingEvaluation } from '../../services/evaluationQueue';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { formatLocalDateInTz, useOrgTimezone } from '../../utils/timezone';
import type { Cultivar, QuickEvaluation, QuickEvaluationGroup } from '../../types/cultivar';

// Quick evaluations of cultivars not in the registry, grouped by the name on
// the tag, waiting to be matched to a registered cultivar (or registered).
// Matching turns each into an ordinary evaluation with its photos; the server
// decides whether this organisation may evaluate that cultivar.

type Tab = 'unmatched' | 'matched';

function errorMessage(e: unknown, fallback: string): string {
  const err = e as { response?: { data?: { error?: { message?: string } } }; message?: string };
  return err.response?.data?.error?.message || err.message || fallback;
}

function QuickPhoto({ id }: { id: number }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let revoked = false;
    let made: string | null = null;
    cultivarService.fetchQuickPhotoBlobUrl(id)
      .then((u) => { if (revoked) URL.revokeObjectURL(u); else { made = u; setUrl(u); } })
      .catch(() => { /* a missing thumbnail is not worth an error */ });
    return () => { revoked = true; if (made) URL.revokeObjectURL(made); };
  }, [id]);
  return url
    ? <img src={url} alt="" className="h-20 w-20 rounded object-cover border border-neutral-200" />
    : <div className="h-20 w-20 rounded bg-neutral-100 border border-neutral-200" />;
}

function VisitRow({ q, tz, onDelete }: { q: QuickEvaluation; tz: string; onDelete?: () => void }) {
  return (
    <div className="border-t border-neutral-100 pt-3 space-y-2">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 text-sm text-neutral-800">
          <span className="font-medium">{formatLocalDateInTz(q.evaluated_on, tz)}</span>
          {q.name_seen && <span className="text-neutral-500"> · tag "{q.name_seen}"</span>}
          {q.location_label && <span className="text-neutral-600 break-words"> · {q.location_label}</span>}
          {q.overall_score != null && <span className="text-primary-700 font-medium"> · {q.overall_score}/10</span>}
          {q.observed_by_name && <span className="text-neutral-500"> · {q.observed_by_name}</span>}
        </div>
        {onDelete && (
          <button onClick={onDelete} className="text-neutral-400 hover:text-error-600 p-1" aria-label="Delete quick evaluation">
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </div>
      {q.notes && <p className="text-sm text-neutral-700 whitespace-pre-wrap break-words">{q.notes}</p>}
      {q.photos.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {q.photos.map((p) => <QuickPhoto key={p.id} id={p.id} />)}
        </div>
      )}
    </div>
  );
}

function FindCultivar({ onPick }: { onPick: (c: Cultivar) => void }) {
  const [text, setText] = useState('');
  const [results, setResults] = useState<Cultivar[]>([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const s = text.trim();
    if (s.length < 2) { setResults([]); return; }
    let cancelled = false;
    setBusy(true);
    const t = setTimeout(() => {
      cultivarService.searchCultivars({ search: s, limit: 8 })
        .then((res) => { if (!cancelled && res.success && res.data) setResults(res.data.cultivars); })
        .catch(() => { if (!cancelled) setResults([]); })
        .finally(() => { if (!cancelled) setBusy(false); });
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
  }, [text]);
  return (
    <div className="space-y-2">
      <label className="block text-xs font-medium text-neutral-700">
        Find the cultivar in the registry
        <input
          type="text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Name, trade name or code"
          className="input-professional w-full mt-1"
        />
      </label>
      {busy && <Loader2 className="h-4 w-4 animate-spin text-neutral-400" />}
      {results.map((c) => (
        <button
          key={c.id}
          onClick={() => onPick(c)}
          className="block w-full text-left text-sm px-3 py-2 rounded border border-neutral-200 hover:bg-neutral-50 break-words"
        >
          {c.name}{c.crop_type_name ? <span className="text-neutral-500"> · {c.crop_type_name}</span> : null}
        </button>
      ))}
    </div>
  );
}

function QuickEvaluations() {
  const navigate = useNavigate();
  const tz = useOrgTimezone();
  const { hasAccess: canRegister } = useModuleLicense('cultivar', ['pbr', 'admin']);
  const [tab, setTab] = useState<Tab>('unmatched');
  const [groups, setGroups] = useState<QuickEvaluationGroup[]>([]);
  const [pending, setPending] = useState<PendingEvaluation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ text: string; cultivarId: number } | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [formName, setFormName] = useState<{ name?: string; crop?: number | null }>({});
  const [finding, setFinding] = useState<string | null>(null);
  const [matching, setMatching] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await cultivarService.listQuickEvaluations(tab);
      if (res.success && res.data) setGroups(res.data);
      setError(null);
    } catch (e) {
      setError(errorMessage(e, 'Could not load quick evaluations.'));
    } finally {
      setLoading(false);
    }
  }, [tab]);

  const loadPending = useCallback(async () => {
    try {
      setPending((await evaluationQueue.listPending()).filter((p) => p.quick));
    } catch { /* IndexedDB unavailable: nothing queued to show */ }
  }, []);

  useEffect(() => { setLoading(true); void load(); }, [load]);

  // Sends anything saved offline, and refreshes once it lands.
  useEffect(() => {
    evaluationQueue.startAutoSync();
    void loadPending();
    let last = evaluationQueue.getStatus().lastSuccessAt;
    return evaluationQueue.subscribeStatus((s) => {
      void loadPending();
      if (s.lastSuccessAt && s.lastSuccessAt !== last) {
        last = s.lastSuccessAt;
        void load();
      }
    });
  }, [load, loadPending]);

  const match = async (group: QuickEvaluationGroup, cultivarId: number, cultivarName: string) => {
    const ids = group.evaluations.filter((e) => !e.matched_at).map((e) => e.id);
    if (!ids.length) return;
    setMatching(group.name_key);
    setError(null);
    try {
      const res = await cultivarService.matchQuickEvaluations(ids, cultivarId);
      if (res.success && res.data) {
        const n = res.data.matched.length;
        setNotice({ text: `${n === 1 ? 'One evaluation' : `${n} evaluations`} of "${group.name_seen}" added to ${cultivarName}.`, cultivarId });
        setFinding(null);
        await load();
      }
    } catch (e) {
      setError(errorMessage(e, 'Could not match them.'));
    } finally {
      setMatching(null);
    }
  };

  const remove = async (q: QuickEvaluation) => {
    if (!window.confirm(`Delete the quick evaluation of "${q.name_seen}" on ${formatLocalDateInTz(q.evaluated_on, tz)} and its photos?`)) return;
    try {
      await cultivarService.deleteQuickEvaluation(q.id);
      await load();
    } catch (e) {
      setError(errorMessage(e, 'Could not delete it.'));
    }
  };

  const register = (group: QuickEvaluationGroup) => {
    const qs = new URLSearchParams({ quick: group.name_key, name: group.name_seen });
    if (group.crop_type_id) qs.append('crop_type_id', String(group.crop_type_id));
    navigate(`/cultivar/create?${qs.toString()}`);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title="Quick evaluations"
        subtitle="Notes and photos of cultivars not in the registry yet"
        icon={<ClipboardPen className="h-6 w-6 text-white" />}
        actionButtons={
          <Button variant="primary" onClick={() => { setFormName({}); setFormOpen(true); }}>
            <Plus className="h-4 w-4" /> Quick evaluation
          </Button>
        }
      />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-4">
        <p className="text-sm text-neutral-600">
          Only your organisation sees these. Once the cultivar is in the registry, match it here and each one
          becomes an evaluation of that cultivar, photos included.
        </p>

        {pending.length > 0 && (
          <div className="bg-warning-50 border border-warning-200 rounded-lg p-3 text-sm text-warning-800 flex flex-wrap items-center gap-2">
            <CloudUpload className="h-4 w-4 shrink-0" />
            <span className="flex-1 min-w-0">
              {pending.length === 1 ? 'One quick evaluation is' : `${pending.length} quick evaluations are`} on this
              device waiting for signal: {pending.map((p) => p.cultivarName).join(', ')}.
            </span>
            <Button variant="outline" size="sm" onClick={() => evaluationQueue.flush()}>Send now</Button>
          </div>
        )}

        {notice && (
          <div className="bg-success-50 border border-success-200 rounded-lg p-3 text-sm text-success-800 flex items-start gap-2">
            <CheckCircle className="h-4 w-4 mt-0.5 shrink-0" />
            <span>
              {notice.text} <Link to={`/cultivar/${notice.cultivarId}?tab=observations`} className="underline font-medium">Open it</Link>
            </span>
          </div>
        )}
        {error && <div className="bg-error-50 border border-error-200 rounded-lg p-3 text-sm text-error-700">{error}</div>}

        <div className="flex gap-2" role="tablist">
          {(['unmatched', 'matched'] as Tab[]).map((t) => (
            <button
              key={t}
              role="tab"
              aria-selected={tab === t}
              onClick={() => setTab(t)}
              className={`px-3 py-1.5 rounded-full text-sm border ${
                tab === t ? 'bg-primary-600 text-white border-primary-600' : 'bg-white text-neutral-700 border-neutral-300'
              }`}
            >
              {t === 'unmatched' ? 'Waiting to match' : 'Matched'}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-primary-600" /></div>
        ) : groups.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center text-sm text-neutral-600">
              {tab === 'unmatched'
                ? 'Nothing waiting. Walking a trial block? Add a quick evaluation for each tree that is not in the registry.'
                : 'Nothing matched yet.'}
            </CardContent>
          </Card>
        ) : groups.map((g) => {
          const open = g.evaluations.filter((e) => !e.matched_at);
          const matched = g.evaluations.find((e) => e.matched_cultivar_id);
          return (
            <Card key={g.name_key}>
              <CardContent className="p-4 space-y-3">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h3 className="text-base font-semibold text-neutral-900 break-words">{g.name_seen}</h3>
                    <p className="text-xs text-neutral-500">
                      {g.crop_type_name || 'Crop not given'} · {g.evaluations.length === 1 ? 'one visit' : `${g.evaluations.length} visits`}
                    </p>
                  </div>
                  {tab === 'unmatched' && (
                    <Button variant="outline" size="sm" onClick={() => { setFormName({ name: g.name_seen, crop: g.crop_type_id }); setFormOpen(true); }}>
                      <Plus className="h-4 w-4" /> Another visit
                    </Button>
                  )}
                </div>

                {matched && tab === 'matched' && (
                  <p className="text-sm text-success-800 flex items-center gap-1.5">
                    <Link2 className="h-4 w-4 shrink-0" />
                    <span>Now evaluations of <Link to={`/cultivar/${matched.matched_cultivar_id}?tab=observations`} className="underline font-medium">{matched.matched_cultivar_name}</Link></span>
                  </p>
                )}

                {open.length > 0 && (
                  <div className="space-y-2">
                    {g.suggestions.length > 0 ? (
                      <div className="space-y-1">
                        <p className="text-xs font-medium text-neutral-700">In the registry, it may be:</p>
                        {g.suggestions.map((s) => (
                          <div key={s.id} className="flex flex-wrap items-center gap-2 text-sm">
                            <Link to={`/cultivar/${s.id}`} className="font-medium text-primary-700 underline break-words">{s.name}</Link>
                            <span className="text-xs text-neutral-500">
                              {s.exact ? 'same name' : 'similar name'}
                              {s.matched_synonym ? `, also called ${s.matched_synonym}` : ''}
                              {s.crop_type_name ? ` · ${s.crop_type_name}` : ''}
                            </span>
                            <Button
                              variant="primary"
                              size="sm"
                              isLoading={matching === g.name_key}
                              onClick={() => match(g, s.id, s.name)}
                            >
                              <Link2 className="h-4 w-4" /> It's this one
                            </Button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-neutral-500">Nothing in the registry reads the same yet.</p>
                    )}
                    <div className="flex flex-wrap gap-2">
                      <Button variant="outline" size="sm" onClick={() => setFinding(finding === g.name_key ? null : g.name_key)}>
                        <Search className="h-4 w-4" /> Find another
                      </Button>
                      {canRegister && (
                        <Button variant="outline" size="sm" onClick={() => register(g)}>
                          <Leaf className="h-4 w-4" /> Register it
                        </Button>
                      )}
                    </div>
                    {finding === g.name_key && <FindCultivar onPick={(c) => match(g, c.id, c.name)} />}
                  </div>
                )}

                {g.evaluations.map((q) => (
                  <VisitRow key={q.id} q={q} tz={tz} onDelete={q.matched_at ? undefined : () => remove(q)} />
                ))}
              </CardContent>
            </Card>
          );
        })}
      </div>

      <QuickEvaluationForm
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSaved={() => { void loadPending(); void load(); }}
        initialName={formName.name}
        initialCropTypeId={formName.crop}
      />
    </div>
  );
}

export default QuickEvaluations;
